<template>
  <div class="m-betting-insights">
    <ul class="insights-tab">
      <li
        class="team-name"
        :class="{ 'tab-active': curTab === 1 }"
        @click="changeTab(1)"
      >
        {{ event.homeTeam }}
      </li>
      <li
        class="team-name"
        :class="{ 'tab-active': curTab === 2 }"
        @click="changeTab(2)"
      >
        {{ event.awayTeam }}
      </li>
    </ul>

    <div class="insights-filter">
      <div class="insights-filter-left">
        <div class="check">
          <CheckboxSquare
            :value="isTeam"
            @change="(val) => changeCheck('team', val)"
          />
          <span @click="changeCheck('team', isTeam)">{{ curTab === 1 ? 'Home' : 'Away' }}</span>
        </div>
        <div class="check">
          <CheckboxSquare
            :value="isLeague"
            @change="(val) => changeCheck('league', val)"
          />
          <span @click="changeCheck('league', isLeague)">This League</span>
        </div>
      </div>
      <ul class="insights-filter-right">
        <li
          v-for="(num, index) in lastList"
          :key="index"
          :class="{ 'active-last': lastNum === num }"
          @click="changeLast(num)"
        >
          {{ lastNum === num ? `Last ${num}` : num }}
        </li>
      </ul>
    </div>

    <div class="insights-live">
      <VButton
        :class="{ 'btn-active': !isLive }"
        @click="changeType(false)"
      >
        Pre Match
      </VButton>
      <VButton
        :class="{ 'btn-active': isLive, 'btn-live': isLiveMatch(event.status) }"
        @click="changeType(true)"
      >
        <img src="./images/live-icon.gif">
        {{ `${timeRange} (${event.scoreOfWholeMatch})` }}
      </VButton>
    </div>

    <div
      v-block-loading:getInsightsData="loading"
      class="insights-content"
    >
      <template v-if="!loading">
        <div class="insights-content-top">
          <p
            v-if="showLiveText && isLive"
            class="insights-live-text"
          >
            <span>* The following list consists of all matches where the team had <em>the same score within the same duration:</em></span>
            <svg-icon
              icon-class="icon-close"
              @click="closeText"
            />
          </p>
          <div class="insights-history">
            <div class="matches-num">
              Matches
              <span>{{ formatValueShow(curEvents.length) }}</span>
            </div>
            <ul class="matches-data">
              <li>
                Won
                <span>{{ formatValueShow(curInsights && curInsights.stageStat ? curInsights.stageStat.wonPercent : null) }}</span>
              </li>
              <li>
                Avg. Total
                <span>{{ formatValueShow(curInsights && curInsights.stageStat ? curInsights.stageStat.avgTotal : null) }}</span>
              </li>
              <li>
                GG
                <span>{{ formatValueShow(curInsights && curInsights.stageStat ? curInsights.stageStat.ggPercent : null) }}</span>
              </li>
            </ul>
            <div
              class="animate-wrap"
              :class="{
                'start-animate': shouldAnimationBackground
              }"
            />
          </div>
        </div>
        <template v-if="curEvents.length">
          <div class="insights-events">
            <ul class="insights-events-lable">
              <li />
              <li
                v-if="isLive"
                class="live-time"
              >
                {{ timeRange }}
              </li>
              <li
                v-else
                style="width: 0; margin: 0;"
              />
              <li>FT</li>
              <li>Results</li>
              <li>Total</li>
              <li>GG/NG</li>
            </ul>
            <div
              v-for="match in curEvents"
              :key="match.eventId"
              class="insights-events-item"
            >
              <p class="insights-events-time">
                <span>{{ showTime(match.startTime) }}</span>
                {{ match.category }}-{{ match.tournament }}
              </p>
              <div class="insights-events-box">
                <Event
                  :event="match"
                />
                <div
                  v-if="isLive"
                  class="event-live"
                >
                  <span>{{ event.scoreOfWholeMatch.split(':')[0] }}</span>
                  <span>{{ event.scoreOfWholeMatch.split(':')[1] }}</span>
                </div>
                <div class="event-ft">
                  <span>{{ match.homeScoreWholeMatch }}</span>
                  <span>{{ match.awayScoreWholeMatch }}</span>
                </div>
                <Result :status="match.result" />
                <span class="event-total">{{ Number(match.homeScoreWholeMatch) + Number(match.awayScoreWholeMatch) }}</span>
                <span class="event-flag">{{ Number(match.homeScoreWholeMatch) && Number(match.awayScoreWholeMatch) ? 'Yes' : 'No' }}</span>
              </div>
            </div>
          </div>

          <div class="insights-stats">
            <ul class="insights-stats-lable">
              <li>Stats</li>
              <li v-if="!isPrematch(event.status) && isLive">
                This match
              </li>
              <li>Avg. Stats per Goal</li>
            </ul>
            <ul
              v-for="table in tables"
              :key="table.action"
              class="insights-stats-table"
            >
              <li>
                {{ table.action === 1 ? 'Shots' : 'Shots on Target' }}
              </li>
              <li
                v-if="isLive"
                :class="{'live-data': isLiveMatch(event.status)}"
              >
                {{ formatValueShow(table.liveStat) }}
              </li>
              <li>{{ formatValueShow(table.recentAvgStat) }}</li>
            </ul>

            <p class="insights-stats-text">
              * 'Avg. Stats per Goal' is the average stat of the full time match for the selected team based on the matches filtered above.
            </p>
          </div>
        </template>
        <Empty
          v-else
          text="There are no matches available."
        />
      </template>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, watch, onMounted } from '@vue/composition-api'
import { storeToRefs } from 'pinia'
import dayjs from 'dayjs'
import { useBetInsightsStore } from '@ms/stores/betInsights'
import { isPrematch, isLiveMatch } from '@ms/components/match/status.js'
import { getTimeRange } from './getTimeRange'
import VButton from 'fe-vui/button'
import CheckboxSquare from '@ms/components/Checkbox/CheckboxSquare.vue'
import Empty from '@ms/components/Empty'
import Event from './components/Event'
import Result from './components/Result'
import 'base/icons/svg/close.svg'

export default {
  name: 'BettingInsights',
  components: {
    CheckboxSquare,
    Empty,
    Event,
    Result,
    VButton
  },
  props: {
    event: {
      type: Object,
      default: () => ({})
    }
  },
  setup(props) {
    const lastList = [5, 10, 20]

    const { insights } = storeToRefs(useBetInsightsStore())

    const timeRange = computed(() => getTimeRange(props.event.totalPlayedTime || ''))
    const score = computed(() => props.event.scoreOfWholeMatch)

    const loading = ref(false)
    const animateTimer = ref(null)
    const curTab = ref(1)
    const lastNum = ref(5)
    const isTeam = ref(true)
    const isLeague = ref(false)
    const isLive = ref(true)
    const showLiveText = ref(false)
    const homePrematchData = ref(null)
    const awayPrematchData = ref(null)
    const homeLiveData = ref(null)
    const awayLiveData = ref(null)
    const filterOptions = reactive({
      isHome: true,
      teamSorting: 1,
      sameTournament: 2,
      preMatch: 2,
      event: props.event
    })

    const resetData = () => {
      homePrematchData.value = null
      awayPrematchData.value = null
      homeLiveData.value = null
      awayLiveData.value = null
    }

    const curInsights = computed(() => {
      let data = null
      const { teamSorting, sameTournament, preMatch } = filterOptions
      const id = `${teamSorting}#${sameTournament}#${lastNum.value}`

      if (curTab.value === 1) {
        if (preMatch === 2) data = homeLiveData.value
        else data = homePrematchData.value
      } else {
        if (preMatch === 2) data = awayLiveData.value
        else data = awayPrematchData.value
      }
      console.log(25, id)
      return data && JSON.stringify(data.stageStat) !== '{}' ? { stageStat: data.stageStat[id], events: data.events } : null
    })

    const curEvents = computed(() => {
      if (!curInsights.value || !curInsights.value.stageStat) return []
      if (curInsights.value) {
        const { ids } = curInsights.value.stageStat
        const { events } = curInsights.value
        const list = []
        ids.forEach((x) => {
          events.forEach((event) => {
            if (event.eventId === x) {
              list.push(event)
            }
          })
        })

        return list
      }

      return []
    })

    const tables = computed(() => (curInsights.value && curInsights.value.stageStat ? curInsights.value.stageStat.tables : []))

    watch(timeRange, () => {
      resetData()
      getInsightsData(filterOptions, false)
    })

    watch(score, () => {
      resetData()
      getInsightsData(filterOptions, false)
    })

    const formatValueShow = (val) => (val === null ? '-' : val)

    const isPreHomeEmpty = () => filterOptions.preMatch === 1 && curTab.value === 1 && !homePrematchData.value
    const isPreAwayEmpty = () => filterOptions.preMatch === 1 && curTab.value === 2 && !awayPrematchData.value
    const isLiveHomeEmpty = () => filterOptions.preMatch === 2 && curTab.value === 1 && !homeLiveData.value
    const isLiveAwayEmpty = () => filterOptions.preMatch === 2 && curTab.value === 2 && !awayLiveData.value

    const changeTab = async (value) => {
      if (value === curTab.value) return false
      curTab.value = value
      filterOptions.isHome = value === 1
      if (isTeam.value) {
        filterOptions.teamSorting = value
      }

      if (isPreHomeEmpty() || isPreAwayEmpty() || isLiveHomeEmpty() || isLiveAwayEmpty()) {
        await getInsightsData(filterOptions)

        if (value === 1) {
          if (isLiveHomeEmpty()) homeLiveData.value = insights.value
          else if (isPreHomeEmpty()) homePrematchData.value = insights.value
        } else {
          if (isLiveAwayEmpty()) awayLiveData.value = insights.value
          else if (isPreAwayEmpty()) awayPrematchData.value = insights.value
        }
      }
    }

    const shouldAnimationBackground = ref(false)

    const changeLast = (value) => {
      if (value === lastNum.value) return false
      lastNum.value = value

      if (animateTimer.value !== null) {
        return
      }

      shouldAnimationBackground.value = true
      animateTimer.value = setTimeout(() => {
        clearTimeout(animateTimer.value)
        animateTimer.value = null
        shouldAnimationBackground.value = false
      }, 1000)
    }

    const changeCheck = (type, status) => {
      if (type === 'team') {
        isTeam.value = !status
        if (!status) {
          filterOptions.teamSorting = curTab.value
        } else {
          filterOptions.teamSorting = 0
        }
      } else {
        isLeague.value = !status
        filterOptions.sameTournament = isLeague.value ? 1 : 2
      }
    }

    const changeType = async (status) => {
      if (isLive.value === status) return false
      isLive.value = status
      if (status) filterOptions.preMatch = 2
      else filterOptions.preMatch = 1

      if (isPreHomeEmpty() || isPreAwayEmpty() || isLiveHomeEmpty() || isLiveAwayEmpty()) {
        await getInsightsData(filterOptions)

        if (status) {
          if (isLiveHomeEmpty()) homeLiveData.value = insights.value
          else if (isLiveAwayEmpty()) awayLiveData.value = insights.value
        } else {
          if (isPreHomeEmpty()) homePrematchData.value = insights.value
          else if (isPreAwayEmpty()) awayPrematchData.value = insights.value
        }
      }
    }

    const getInsightsData = async (params = null, isLoading = true) => {
      try {
        isLoading && (loading.value = true)
        const _params = params || filterOptions
        await useBetInsightsStore().getInsights(_params)

        const { preMatch } = filterOptions
        if (curTab.value === 1) {
          if (preMatch === 2) homeLiveData.value = insights.value
          else homePrematchData.value = insights.value
        } else {
          if (preMatch === 2) awayLiveData.value = insights.value
          else awayPrematchData.value = insights.value
        }
        loading.value = false
      } catch (error) {
        isLoading && (loading.value = -1)
        console.error(error)
      }
    }

    const closeText = () => {
      showLiveText.value = false
      localStorage.setItem('LIVE_INSIGHTS_TEXT', '1')
    }

    onMounted(() => {
      getInsightsData(filterOptions)
      const showText = localStorage.getItem('LIVE_INSIGHTS_TEXT')
      showLiveText.value = !showText
    })

    return {
      loading,
      curInsights,
      curEvents,
      tables,
      lastList,
      lastNum,
      isLive,
      isTeam,
      isLeague,
      curTab,
      timeRange,
      isPrematch,
      isLiveMatch,
      showLiveText,
      changeTab,
      changeCheck,
      changeLast,
      changeType,
      closeText,
      formatValueShow,
      getInsightsData,
      shouldAnimationBackground,
      showTime: (time) => dayjs(time).format('DD.MM.YYYY HH:mm')
    }
  }
}
</script>

<style lang="scss">
@use "@ms/styles/_mixin.scss" as mixin;

@keyframes fade-in-out {
  0% {
    opacity: 1;
  }

  50% {
    opacity: 0;
  }

  100% {
    opacity: 1;
  }
}

.m-betting-insights {
  margin-bottom: -20px;

  * {
    user-select: none;
  }

  .insights-tab {
    display: flex;
    width: 100%;
    height: 44px;
    border-bottom: 1px solid #333;
    background: #212121;
    line-height: 44px;

    .team-name {
      width: 50%;
      height: 100%;
      box-sizing: border-box;
      padding: 16px;
      color: #999;
      font-size: 14px;
      font-weight: 600;
      line-height: 100%;
      text-align: center;

      @include mixin.ellipsis();
    }

    .tab-active {
      position: relative;
      color: #ffca27;

      &::after {
        position: absolute;
        bottom: 0;
        left: 0;
        width: 100%;
        height: 2px;
        border-radius: 1px;
        background: #ffca27;
        content: "";
      }
    }
  }

  .insights-filter {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 12px;
    background: #212121;

    &-left {
      display: flex;
      flex: 1;

      @include mixin.ellipsis();
    }

    .check {
      display: flex;
      max-width: 52%;
      color: #ccc;
      font-size: 12px;
      line-height: 16px;

      @include mixin.ellipsis();

      span {
        @include mixin.ellipsis();
      }

      &:first-child {
        max-width: 48%;
        margin-right: 10px;

        span {
          display: block;
          width: 33px;
        }
      }

      .checkbox {
        min-width: 16px;
        margin-right: 4px;
      }
    }

    &-right {
      display: flex;
      height: 28px;
      box-sizing: border-box;
      border: 2px solid #000;
      border-radius: 14px;
      background: #000;
      line-height: 24px;
      text-align: center;

      li {
        width: 54px;
        box-sizing: border-box;
        color: #ccc;
        font-size: 12px;
        text-align: center;
        white-space: nowrap;
      }

      .active-last {
        border-color: rgba(0, 0, 0, 4%);
        border-radius: 12px;
        background: #292929;
        box-shadow: 0 1px 7px rgba(0, 0, 0, 30%), 0 0 9px rgba(0, 0, 0, 12%);
        color: #fff;
        font-weight: 500;
      }
    }
  }

  .insights-live {
    display: flex;
    padding: 0 12px;
    background: #212121;

    .v-button {
      width: auto;
      height: 22px;
      box-sizing: border-box;
      margin-right: 10px;
      background: unset;
      color: #999;
      font-size: 12px;
      font-weight: 500;
      line-height: 22px;

      &.btn-active {
        border: 1px solid #ffca27;
        color: #ffca27 !important;

        .mp-loading-lines {
          stroke: #ffca27 !important;
        }
      }

      &.btn-live {
        color: #ffca27;
      }

      img {
        width: 8px;
        margin: -1px 5px 0 0;
      }
    }

    span {
      display: flex;
      align-items: center;
    }
  }

  .insights-content {
    .insights-content-top {
      padding: 16px 0 20px;
      background: #212121;
    }

    .insights-live-text {
      display: flex;
      min-height: 28px;
      align-items: center;
      justify-content: space-between;
      padding: 8px 16px;
      margin-bottom: 16px;
      background: #2b2b2b;
      color: #999;
      font-size: 12px;
      line-height: 14px;

      span {
        flex: 1;
      }

      em {
        color: #ccc;
      }

      svg {
        width: 16px;
        height: 16px;
        margin-left: 20px;
      }
    }
  }

  .insights-history {
    position: relative;
    display: flex;
    height: 42px;
    justify-content: space-between;
    padding: 0 16px 0 20px;
    margin-top: 3px;
    // background: #3b3627;
    text-align: center;

    .matches-num {
      position: relative;
      z-index: 2;
      display: inline-block;
      overflow: hidden;
      width: 65px;
      height: 49px;
      box-sizing: border-box;
      padding: 1px 2px;
      border-radius: 6px;
      margin-top: -4px;
      background: #ffca27;
      color: rgba(0, 0, 0, 80%);
      font-family: Roboto;
      font-size: 13px;
      font-style: italic;
      font-weight: 900;
      letter-spacing: -0.09px;
      line-height: 22px;

      span {
        display: block;
        width: 61px;
        height: 23px;
        border-radius: 4px;
        margin-top: 1px;
        background: #231f13;
        color: #ffca27;
        font-size: 16px;
        font-style: normal;
        font-weight: 600;
      }
    }

    .matches-data {
      position: relative;
      z-index: 2;
      display: flex;
      margin-top: 3px;

      li {
        width: 52px;
        color: #999;
        font-size: 12px;
        letter-spacing: -0.09px;
        line-height: 14px;
        text-align: center;
        white-space: nowrap;

        &:first-child {
          width: 42px;
          margin-right: 4px;
        }

        &:last-child {
          width: 42px;
          margin-left: 9px;
        }

        span {
          display: block;
          margin-top: 1px;
          color: #fff;
          font-size: 16px;
          font-weight: 500;
          line-height: 20px;
        }
      }
    }

    .animate-wrap {
      position: absolute;
      z-index: 1;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: #3b3627;
      opacity: 1;

      &.start-animate {
        animation: fade-in-out 1s linear;
      }
    }
  }

  .insights-events {
    border-top: 1px solid #000;

    &-lable {
      display: flex;
      height: 30px;
      padding: 0 16px;
      background: #212121;
      color: #ccc;
      font-size: 12px;
      line-height: 30px;
      text-align: center;

      li {
        &.live-time {
          color: #ffca27;
        }

        &:first-child {
          flex: 1;
        }

        &:nth-of-type(2) {
          width: 40px;
          margin-right: 11px;
        }

        &:nth-of-type(3) {
          width: 40px;
          margin-right: 11px;
        }

        &:nth-of-type(4) {
          width: 42px;
          margin-right: 4px;
        }

        &:nth-of-type(5) {
          width: 52px;
        }

        &:last-child {
          width: 42px;
          margin-right: 0;
          margin-left: 9px;
        }
      }
    }

    &-item {
      padding: 8px 16px 0;
      background: #000;
      color: #fff;

      &:last-child {
        .insights-events-box {
          border: none;
        }
      }

      .m-event-wrap {
        max-width: calc(229 / 335) * 100%;
        flex: 1;
        padding: 10px 0;
        margin-right: 9px;

        &::after {
          display: none;
        }
      }

      .m-event-status {
        display: none;
      }

      .special-event-status {
        display: none;
      }

      .m-event-teams {
        width: calc(121 / 229) * 100%;
        margin: 0 9px 0 0;
        text-align: left;
      }

      .m-event-score {
        width: calc(30 / 229) * 100%;
        min-width: 30px;
        margin: 0 9px;
        text-align: center;
      }

      .match-scores {
        display: unset;
        text-align: center;

        .m-score-wrap {
          margin-left: 0;
        }
      }

      .result-text {
        width: calc(42 / 229) * 100%;
        min-width: 42px;
        margin: 0 0 0 9px;

        span {
          margin: 0 auto;
        }
      }
    }

    .insights-events-time {
      color: #666;
      font-size: 10px;
      line-height: 12px;
      text-align: left;

      @include mixin.ellipsis();

      span {
        margin-right: 8px;
      }
    }

    .insights-events-box {
      display: flex;
      align-items: center;
      border-bottom: 1px solid #1a1a1a;
      font-size: 14px;
      text-align: center;

      .event-live {
        color: #ffca27;
      }

      .event-live,
      .event-ft {
        width: 42px;
        margin-right: 11px;

        span {
          display: block;

          &:first-child {
            margin-bottom: 2px;
          }
        }
      }

      .event-result {
        width: 42px;
        margin-right: 4px;
      }

      .event-total {
        width: 52px;
      }

      .event-flag {
        width: 42px;
        margin: 0 0 0 9px;
      }

      .m-event-teams--home,
      .m-event-teams--away {
        span {
          @include mixin.ellipsis();
        }
      }
    }
  }

  .insights-stats {
    &-lable {
      display: flex;
      height: 30px;
      padding: 0 20px;
      background: #212121;
      color: #ccc;
      font-size: 12px;
      line-height: 30px;

      li {
        &:first-child {
          flex: 1;
        }

        &:nth-of-type(2) {
          width: 64px;
          margin: 0 33px 0 0;
        }

        &:last-child {
          width: 110px;
          margin-right: 0;
        }
      }
    }

    &-table {
      position: relative;
      display: flex;
      padding: 8px 20px;
      background: #000;
      color: #fff;
      font-size: 14px;
      line-height: 18px;

      li {
        &:first-child {
          flex: 1;

          @include mixin.ellipsis();
        }

        &:nth-of-type(2) {
          width: 64px;
          margin: 0 33px 0 0;
          text-align: center;

          &.live-data {
            color: #ffca27;
          }
        }

        &:last-child {
          width: 110px;
          margin-right: 0;
          text-align: center;
        }
      }

      &::after {
        position: absolute;
        bottom: 0;
        display: block;
        width: calc(100% - 40px);
        border-bottom: 1px solid #1a1a1a;
        content: "";
      }
    }

    &-text {
      padding: 8px 16px 0;
      padding-bottom: 30px;
      background: #000;
      color: #666;
      font-size: 12px;
      line-height: 16px;
    }
  }

  &.insights-web {
    .insights-tab .team-name {
      cursor: pointer;

      &:hover {
        color: #fff;
      }

      &.tab-active:hover {
        color: #ffca27;
      }
    }

    .insights-filter .check {
      cursor: pointer;
    }

    .v-button {
      &:hover {
        color: #ffca27;
      }
    }

    .btn-live {
      &:hover {
        color: #fff0c4;

        .mp-loading-lines {
          stroke: #fff0c4;
        }
      }
    }

    .insights-filter-right li {
      cursor: pointer;
    }

    .insights-history {
      padding-right: 20px;
    }

    .matches-data {
      li {
        &:first-child {
          margin-right: 19px;
        }

        &:last-child {
          margin-left: 20px;
        }
      }
    }

    .insights-events-lable {
      padding: 0 20px;

      li {
        &:nth-of-type(2) {
          margin-right: 12px;
        }

        &:nth-of-type(3) {
          margin-right: 23px;
        }

        &:nth-of-type(4) {
          margin-right: 19px;
        }

        &:last-child {
          margin-left: 20px;
        }
      }
    }

    .insights-events-item {
      padding: 12px 20px 0;
    }

    .insight-event-home {
      margin-bottom: 4px;
    }

    .insights-events-box {
      padding-bottom: 4px;
      border-color: #333;

      .event-live {
        margin-right: 12px;
      }

      .event-ft {
        margin-right: 23px;
      }

      .event-result {
        width: 42px;
        margin-right: 19px;
      }

      .event-total {
        width: 52px;
      }

      .event-flag {
        width: 42px;
        margin: 0 0 0 20px;
      }
    }

    .insight-event {
      font-size: 14px;
    }

    .insights-events-item,
    .insights-stats-table,
    .insights-stats-text {
      background: transparent;
    }

    .insights-stats-lable,
    .insights-stats-table {
      padding-right: 119px;
    }

    .insights-stats-text {
      padding-bottom: 0;
    }
  }

  .checkbox-square-wrap {
    cursor: pointer;
  }
}

@media screen and (min-width: 1440px) {
  .m-betting-insights {
    .insights-filter {
      .check {
        span {
          overflow: unset;
          width: auto;
          text-overflow: unset;
          white-space: unset;
        }

        &:first-child {
          span {
            overflow: unset;
            width: auto;
            text-overflow: unset;
            white-space: unset;
          }
        }
      }
    }
  }
}
</style>
